// Lazy geometry loader: manifest entry -> (preview first, then) full geometry.
// Decoded geometry is cached per export; in-flight loads are deduplicated.
import type { CurveGeometry, ExportMeta } from './types.ts';
import { decodeCurve } from './format.ts';
import { GENERATORS } from './generators/index.ts';

export type GeometryStage = 'preview' | 'full';
export type GeometryUpdate = (g: CurveGeometry, stage: GeometryStage) => void;

interface Entry {
  full?: CurveGeometry;
  preview?: CurveGeometry;
  inflight?: Promise<CurveGeometry>;
  previewInflight?: Promise<CurveGeometry | null>;
}

const cache = new Map<string, Entry>();
const MAX_CACHED_BYTES = 160 * 1024 * 1024;
const lru: string[] = [];

function entry(id: string): Entry {
  let e = cache.get(id);
  if (!e) { e = {}; cache.set(id, e); }
  return e;
}

function touch(id: string, bytes: number): void {
  const i = lru.indexOf(id);
  if (i >= 0) lru.splice(i, 1);
  lru.push(id);
  let total = 0;
  for (const k of lru) total += cache.get(k)?.full?.coords.byteLength ?? 0;
  while (total > MAX_CACHED_BYTES && lru.length > 1) {
    const victim = lru.shift()!;
    const e = cache.get(victim);
    if (e?.full) { total -= e.full.coords.byteLength; e.full = undefined; }
  }
  void bytes;
}

async function fetchDecode(url: string): Promise<CurveGeometry> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`geometry: HTTP ${r.status} for ${url}`);
  const buf = await r.arrayBuffer();
  return decodeCurve(buf);
}

export function cachedGeometry(exp: ExportMeta): CurveGeometry | undefined {
  return cache.get(exp.id)?.full;
}

/**
 * Resolve geometry for an export. `onUpdate` fires with the preview (if one
 * exists and the full geometry is not cached) and then with the full geometry.
 * Returns the full geometry.
 */
export function getGeometry(exp: ExportMeta, onUpdate?: GeometryUpdate): Promise<CurveGeometry> {
  const e = entry(exp.id);
  if (e.full) {
    onUpdate?.(e.full, 'full');
    return Promise.resolve(e.full);
  }

  const src = exp.source;
  if (src.kind === 'proc') {
    const gen = GENERATORS[src.generator];
    if (!gen) return Promise.reject(new Error(`unknown generator ${src.generator}`));
    if (!e.inflight) {
      e.inflight = new Promise<CurveGeometry>((resolve) => {
        // Yield once so the UI can paint "loading" before a big generate.
        setTimeout(() => {
          const g = gen(src.params);
          e.full = g; e.inflight = undefined;
          touch(exp.id, g.coords.byteLength);
          resolve(g);
        }, 0);
      });
    }
    return e.inflight.then((g) => { onUpdate?.(g, 'full'); return g; });
  }

  // Kick off preview and full in parallel.
  let previewP: Promise<CurveGeometry | null> = Promise.resolve(null);
  if (src.preview) {
    if (e.preview) previewP = Promise.resolve(e.preview);
    else {
      if (!e.previewInflight) {
        e.previewInflight = fetchDecode(src.preview.url)
          .then((g) => { e.preview = g; e.previewInflight = undefined; return g; })
          .catch(() => { e.previewInflight = undefined; return null; });
      }
      previewP = e.previewInflight;
    }
  }
  if (!e.inflight) {
    e.inflight = fetchDecode(src.url).then((g) => {
      e.full = g; e.inflight = undefined;
      touch(exp.id, g.coords.byteLength);
      return g;
    }).catch((err) => { e.inflight = undefined; throw err; });
  }
  const fullP = e.inflight;

  let done = false;
  if (onUpdate) {
    previewP.then((pg) => { if (pg && !done) onUpdate(pg, 'preview'); });
  }
  return fullP.then((g) => { done = true; onUpdate?.(g, 'full'); return g; });
}

/** Warm the cache for neighbouring exports when idle. */
export function prefetchIdle(exps: ExportMeta[]): void {
  const run = () => { for (const x of exps) getGeometry(x).catch(() => {}); };
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
  if (w.requestIdleCallback) w.requestIdleCallback(run, { timeout: 3000 });
  else setTimeout(run, 800);
}
